import React, { useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Testimonial {
  id: string | number;
  initials: string;
  name: string;
  role: string;
  quote: string;
  tags: { text: string; type: 'featured' | 'default' }[];
  stats: { icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; text: string }[];
  avatarGradient: string;
  /** Optional detail page; rendered as a link under the name. */
  href?: string;
  hrefLabel?: string;
  renderLink?: (href: string, children: React.ReactNode) => React.ReactNode;
}

export interface TestimonialStackProps {
  testimonials: Testimonial[];
  /** How many cards to show behind the main card */
  visibleBehind?: number;
  labels?: { previous: string; next: string; goTo: (index: number) => string };
}

const DEFAULT_LABELS = {
  previous: 'Previous testimonial',
  next: 'Next testimonial',
  goTo: (index: number) => `Go to testimonial ${index + 1}`,
};

export const TestimonialStack = ({ testimonials, visibleBehind = 2, labels = DEFAULT_LABELS }: TestimonialStackProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef(0);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const totalCards = testimonials.length;

  const navigate = useCallback(
    (newIndex: number) => {
      setActiveIndex((newIndex + totalCards) % totalCards);
    },
    [totalCards],
  );

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent, index: number) => {
    if (index !== activeIndex) return;
    if ((e.target as HTMLElement).closest('a, button')) return;
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragStartRef.current = clientX;
    cardRefs.current[activeIndex]?.classList.add('is-dragging');
  };

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      setDragOffset(clientX - dragStartRef.current);
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    cardRefs.current[activeIndex]?.classList.remove('is-dragging');
    if (Math.abs(dragOffset) > 50) {
      navigate(activeIndex + (dragOffset < 0 ? 1 : -1));
    }
    setIsDragging(false);
    setDragOffset(0);
  }, [isDragging, dragOffset, activeIndex, navigate]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') navigate(activeIndex + 1);
    if (e.key === 'ArrowLeft') navigate(activeIndex - 1);
  };

  if (!testimonials?.length) return null;

  const tagClasses = (type: 'featured' | 'default') =>
    type === 'featured'
      ? 'bg-primary/10 text-primary border border-primary/30'
      : 'bg-secondary text-secondary-foreground';

  return (
    <div className="testimonials-stack-root relative pb-14" onKeyDown={handleKeyDown}>
      <div className="testimonials-stack relative" aria-roledescription="carousel">
        {testimonials.map((testimonial, index) => {
          const displayOrder = (index - activeIndex + totalCards) % totalCards;
          const isActive = displayOrder === 0;

          const style: CSSProperties = {};
          if (isActive) {
            style.transform = `translateX(${dragOffset}px)`;
            style.opacity = 1;
            style.zIndex = totalCards;
          } else if (displayOrder <= visibleBehind) {
            const scale = 1 - 0.05 * displayOrder;
            const translateY = -2 * displayOrder;
            style.transform = `scale(${scale}) translateY(${translateY}rem)`;
            style.opacity = 1 - 0.3 * displayOrder;
            style.zIndex = totalCards - displayOrder;
          } else {
            style.transform = 'scale(0.8)';
            style.opacity = 0;
            style.zIndex = 0;
          }

          return (
            <article
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              key={testimonial.id}
              className="testimonial-card glass-effect backdrop-blur-xl"
              style={style}
              aria-hidden={!isActive}
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${totalCards}`}
              onMouseDown={(e) => handleDragStart(e, index)}
              onTouchStart={(e) => handleDragStart(e, index)}
            >
              <div className="p-6 md:p-10">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-6">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-display font-semibold text-base"
                      style={{ background: testimonial.avatarGradient }}
                      aria-hidden
                    >
                      {testimonial.initials}
                    </div>
                    <div>
                      <h3 className="font-display text-card-foreground font-semibold text-lg">{testimonial.name}</h3>
                      <p className="font-body text-sm text-muted-foreground mt-0.5">{testimonial.role}</p>
                    </div>
                  </div>
                  {testimonial.href && testimonial.renderLink && isActive
                    ? testimonial.renderLink(
                        testimonial.href,
                        <span className="whitespace-nowrap font-body text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                          {testimonial.hrefLabel}
                        </span>,
                      )
                    : null}
                </div>

                <blockquote className="font-body text-card-foreground/90 leading-relaxed text-base md:text-lg mb-8">
                  <p>&ldquo;{testimonial.quote}&rdquo;</p>
                </blockquote>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-t border-border pt-5 gap-4">
                  <div className="flex flex-wrap gap-2">
                    {testimonial.tags.map((tag) => (
                      <span key={tag.text} className={`font-body text-xs px-2.5 py-1 rounded-md ${tagClasses(tag.type)}`}>
                        {tag.text}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 font-body text-xs text-muted-foreground">
                    {testimonial.stats.map((stat) => {
                      const IconComponent = stat.icon;
                      return (
                        <span key={stat.text} className="flex items-center">
                          <IconComponent className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                          {stat.text}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label={labels.previous}
          onClick={() => navigate(activeIndex - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex gap-2">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.id}
              type="button"
              aria-label={labels.goTo(index)}
              aria-current={activeIndex === index}
              onClick={() => navigate(index)}
              className={`pagination-dot ${activeIndex === index ? 'active' : ''}`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label={labels.next}
          onClick={() => navigate(activeIndex + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
